import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { FaLocationArrow, FaPhone } from "react-icons/fa6";
import { MdEmail } from "react-icons/md";
import TenantContext from "../Context/TenantContext.jsx";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { tenant = {} } = useContext(TenantContext) || {};

  // Destructure tenant config with fallbacks
  const {
    name = 'Smart Nurse',
    branding = {},
    contact = {},
    social = {},
    tagline = 'AI Enabling Health Systems & Clinics with Facility & Nursing Efficiencies.',
    features = {}
  } = tenant;

  const {
    logo = '/logo-bw.png',
    companyName = name,
    primaryColor = '#3498db'
  } = branding;

  const {
    phone = '+(1) 925 336 1097',
    email = 'healthsnai@gmail.com',
    address = 'PO Box 2011 Dublin, CA 94568'
  } = contact;

  const {
    facebook,
    twitter,
    instagram,
    linkedin
  } = social;

  const {
    enableAppointments = true
  } = features;

  return (
    <footer className="bg-white text-black dark:bg-slate-300 border-t border-gray-200 dark:border-slate-200">
      <div className="mx-auto w-full px-4 py-6 sm:py-8 sm:px-6 lg:px-8 max-w-7xl">
        {/* Mobile Compact Layout */}
        <div className="md:hidden">
          {/* Logo & Description - Centered on Mobile */}
          <div className="flex flex-col items-center text-center gap-2 mb-6">
            <Link to="/" className="flex flex-col items-center gap-2">
              <img 
                src={logo} 
                className="h-8" 
                alt={`${companyName} Logo`} 
                onError={(e) => { e.currentTarget.src = '/logo-bw.png'; }} 
              />
              <span className="text-base font-semibold dark:text-black">
                {companyName}
              </span>
            </Link>
            <p className="max-w-xs text-black-500 dark:text-black-400 text-xs px-4">
              {tagline}
            </p>
          </div>

          {/* Quick Links & Contact - Compact Side by Side */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Quick Links - Left Aligned */}
            <div className="flex flex-col items-start">
              <h2 className="mb-2 text-xs font-semibold text-black-900 uppercase tracking-wider dark:text-black">
                Quick Links
              </h2>
              <ul className="text-black-500 dark:text-black-400 text-xs space-y-1.5">
                <li>
                  <Link to="/" className="hover:underline">Home</Link>
                </li>
                <li>
                  <Link to="/dashboard/nurse-chat/answers" className="hover:underline">
                    Assessments
                  </Link>
                </li>
                {enableAppointments && (
                  <li>
                    <Link to="/appointments" className="hover:underline">Appointments</Link>
                  </li>
                )}
                <li>
                  <Link to="/about" className="hover:underline">About</Link>
                </li>
              </ul>
            </div>

            {/* Contact - Left Aligned */}
            <div className="flex flex-col items-start">
              <h2 className="mb-2 text-xs font-semibold text-black-900 uppercase tracking-wider dark:text-black">
                Contact
              </h2>
              <ul className="text-black-500 dark:text-black-400 text-xs space-y-1.5">
                <li className="flex items-start gap-1.5">
                  <FaPhone className="text-xs mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                  <a 
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`} 
                    className="hover:underline break-all"
                  >
                    {phone}
                  </a>
                </li>
                <li className="flex items-start gap-1.5">
                  <MdEmail className="text-xs mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                  <a 
                    className="hover:underline break-all" 
                    href={`mailto:${email}`}
                  >
                    {email}
                  </a>
                </li>
                <li className="flex items-start gap-1.5">
                  <FaLocationArrow className="text-xs mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                  <span className="text-xs">{address}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section - Compact */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col gap-3 items-center">
              <span className="text-xs text-black-500 dark:text-black-400 text-center">
                © {currentYear} {companyName}
              </span>
              
              {/* Social Icons - Smaller on Mobile */}
              <div className="flex justify-center gap-3">
                {facebook && (
                  <a 
                    href={facebook} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-black-500 p-1.5 rounded-full transition-colors duration-200" 
                    style={{ '--hover-color': primaryColor }}
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    aria-label="Facebook"
                  >
                    <FaFacebookF className="w-4 h-4" />
                  </a>
                )}
                {twitter && (
                  <a 
                    href={twitter} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-black-500 p-1.5 rounded-full transition-colors duration-200" 
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    aria-label="Twitter"
                  >
                    <FaTwitter className="w-4 h-4" />
                  </a>
                )}
                {instagram && (
                  <a 
                    href={instagram} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-black-500 p-1.5 rounded-full transition-colors duration-200" 
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    aria-label="Instagram"
                  >
                    <FaInstagram className="w-4 h-4" />
                  </a>
                )}
                {linkedin && (
                  <a 
                    href={linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-black-500 p-1.5 rounded-full transition-colors duration-200" 
                    onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    aria-label="LinkedIn"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Hidden on Mobile */}
        <div className="hidden md:block">
          <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-start">
            {/* Logo & Description */}
            <div className="flex flex-col items-start text-left gap-2">
              <Link to="/" className="flex flex-col items-start gap-2 mb-2">
                <img 
                  src={logo} 
                  className="h-10" 
                  alt={`${companyName} Logo`} 
                  onError={(e) => { e.currentTarget.src = '/logo-bw.png'; }} 
                />
                <span className="text-lg font-semibold whitespace-nowrap dark:text-black">
                  {companyName}
                </span>
              </Link>
              <p className="max-w-xs text-black-500 dark:text-black-400 text-xs">
                {tagline}
              </p>
            </div>

            {/* Quick Links, Contact, Hours */}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 w-full md:w-auto">
              <div className="flex flex-col items-start">
                <h2 className="mb-3 text-xs font-semibold text-black-900 uppercase tracking-wider dark:text-black">
                  Quick Links
                </h2>
                <ul className="text-black-500 dark:text-black-400 text-sm space-y-2">
                  <li>
                    <Link to="/" className="hover:underline text-sm">Home</Link>
                  </li>
                  <li>
                    <Link to="/dashboard/nurse-chat/answers" className="hover:underline text-sm">
                      Patient Assessments
                    </Link>
                  </li>
                  {enableAppointments && (
                    <li>
                      <Link to="/appointments" className="hover:underline text-sm">Appointments</Link>
                    </li>
                  )}
                  <li>
                    <Link to="/about" className="hover:underline text-sm">About</Link>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col items-start">
                <h2 className="mb-3 text-xs font-semibold text-black-900 uppercase tracking-wider dark:text-black">
                  Contact
                </h2>
                <ul className="text-black-500 dark:text-black-400 text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <FaPhone className="text-xs" style={{ color: primaryColor }} />
                    <a 
                      href={`tel:${phone.replace(/[^+\d]/g, '')}`} 
                      className="hover:underline text-sm"
                    >
                      {phone}
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <MdEmail className="text-sm" style={{ color: primaryColor }} />
                    <a 
                      className="text-sm hover:underline" 
                      href={`mailto:${email}`}
                    >
                      {email}
                    </a>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaLocationArrow className="text-xs" style={{ color: primaryColor }} />
                    <span className="text-sm">{address}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <hr className="my-6 border-gray-200 dark:border-gray-700" />
          
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-2">
            <span className="text-sm text-black-500 dark:text-black-400 text-center sm:text-left">
              © {currentYear} {companyName}. All Rights Reserved.
            </span>
            
            <div className="flex justify-center gap-4 mt-2 sm:mt-0">
              {facebook && (
                <a 
                  href={facebook} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-black-500 p-2 rounded-full transition-colors duration-200" 
                  style={{ '--hover-color': primaryColor }}
                  onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  aria-label="Facebook"
                >
                  <FaFacebookF className="w-5 h-5" />
                </a>
              )}
              {twitter && (
                <a 
                  href={twitter} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-black-500 p-2 rounded-full transition-colors duration-200" 
                  onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  aria-label="Twitter"
                >
                  <FaTwitter className="w-5 h-5" />
                </a>
              )}
              {instagram && (
                <a 
                  href={instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-black-500 p-2 rounded-full transition-colors duration-200" 
                  onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  aria-label="Instagram"
                >
                  <FaInstagram className="w-5 h-5" />
                </a>
              )}
              {linkedin && (
                <a 
                  href={linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-black-500 p-2 rounded-full transition-colors duration-200" 
                  onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                  onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
